import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usePermissionModal } from "contexts/PermissionModalContext";
import { 
  formaPagamentoService, 
  CriarFormaPagamentoDto, 
  AlterarFormaPagamentoDto} from "services/formaPagamentoService";

// Query keys
export const formaPagamentoKeys = {
  all: ['formaPagamento'] as const,
  list: (incluirInativas?: boolean) => [...formaPagamentoKeys.all, 'list', { incluirInativas }] as const,
  detail: (id: number) => [...formaPagamentoKeys.all, 'detail', id] as const,
};

// Query para obter todas as formas de pagamento
export const useFormasPagamento = (incluirInativas: boolean = false) => {
  const { getQueryRetryHandler } = usePermissionModal();

  return useQuery({
    queryKey: formaPagamentoKeys.list(incluirInativas),
    queryFn: () => formaPagamentoService.obterFormasPagamento(incluirInativas),
    retry: getQueryRetryHandler({
      queryKey: formaPagamentoKeys.list(incluirInativas),
      defaultMessage: "Você não possui permissão para consultar formas de pagamento."
    }),
  });
};

// Query para obter uma forma de pagamento específica
export const useFormaPagamento = (id: string, enabled: boolean = true) => {
  const { getQueryRetryHandler } = usePermissionModal();

  return useQuery({
    queryKey: formaPagamentoKeys.detail(id),
    queryFn: () => formaPagamentoService.obterFormaPagamento(id),
    enabled: !!id && enabled,
    retry: getQueryRetryHandler({
      queryKey: formaPagamentoKeys.detail(id),
      defaultMessage: "Você não possui permissão para consultar esta forma de pagamento."
    }),
  });
};

// Mutation para criar forma de pagamento
export const useCriarFormaPagamento = () => {
  const queryClient = useQueryClient();
  const { getQueryRetryHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (data: CriarFormaPagamentoDto) => formaPagamentoService.criarFormaPagamento(data),
    onSuccess: () => {
      // Invalidar todas as queries de formas de pagamento
      queryClient.invalidateQueries({ queryKey: formaPagamentoKeys.all });
    },
    retry: getQueryRetryHandler({
      queryKey: formaPagamentoKeys.all,
      defaultMessage: "Você não possui permissão para criar formas de pagamento."
    }),
  });
};

// Mutation para alterar forma de pagamento
export const useAlterarFormaPagamento = () => {
  const queryClient = useQueryClient();
  const { getQueryRetryHandler } = usePermissionModal();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AlterarFormaPagamentoDto }) =>
      formaPagamentoService.alterarFormaPagamento(id, data),
    onSuccess: (_, { id }) => {
      // Invalidar queries específicas
      queryClient.invalidateQueries({ queryKey: formaPagamentoKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: formaPagamentoKeys.all });
    },
    retry: getQueryRetryHandler({
      queryKey: formaPagamentoKeys.all,
      defaultMessage: "Você não possui permissão para alterar formas de pagamento."
    }),
  });
};

// Mutation para excluir forma de pagamento
export const useExcluirFormaPagamento = () => {
  const queryClient = useQueryClient();
  const { getQueryRetryHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (id: string) => formaPagamentoService.excluirFormaPagamento(id),
    onSuccess: (_, id) => {
      // Invalidar queries
      queryClient.invalidateQueries({ queryKey: formaPagamentoKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: formaPagamentoKeys.all });
    },
    retry: getQueryRetryHandler({
      queryKey: formaPagamentoKeys.all,
      defaultMessage: "Você não possui permissão para excluir formas de pagamento."
    }),
  });
};

// Mutation para reativar forma de pagamento
export const useReativarFormaPagamento = () => {
  const queryClient = useQueryClient();
  const { getQueryRetryHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (id: string) => formaPagamentoService.reativarFormaPagamento(id),
    onSuccess: (_, id) => {
      // Invalidar queries
      queryClient.invalidateQueries({ queryKey: formaPagamentoKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: formaPagamentoKeys.all });
    },
    retry: getQueryRetryHandler({
      queryKey: formaPagamentoKeys.all,
      defaultMessage: "Você não possui permissão para reativar formas de pagamento."
    }),
  });
};

// Query para obter contas no contexto de formas de pagamento
export const useContasFormaPagamento = () => {
  const { getQueryRetryHandler } = usePermissionModal();

  return useQuery({
    queryKey: ['formaPagamento', 'contas'],
    queryFn: () => formaPagamentoService.obterContasParaFormaPagamento(),
    retry: getQueryRetryHandler({
      queryKey: ['formaPagamento', 'contas'],
      defaultMessage: "Você não possui permissão para consultar contas no contexto de formas de pagamento."
    }),
  });
};